/**
 * What each page's ⓘ dialog lists: its tasks in order, each with an icon, the
 * control "Show me" points at, and who gets to see the task at all. The words
 * live in `t.pageInfo.pages` (src/lib/i18n/translations/{en,et}/pageInfo.ts),
 * keyed by the same ids.
 *
 * Gates mirror the control's own gate (the same `usePermissions` helper), so a
 * reader is never told about an action the page would refuse them. A
 * project-scoped gate needs a project; on a whole-building page there is none,
 * and the role-level grant of the same code answers instead.
 */
import {
  Activity,
  AlertTriangle,
  Archive,
  ArchiveRestore,
  ArrowLeftRight,
  ArrowRightLeft,
  ArrowUpDown,
  BadgeCheck,
  BarChart3,
  Bell,
  BellDot,
  BookOpen,
  Bot,
  Box,
  Building,
  Building2,
  CalendarClock,
  Camera,
  Check,
  CheckCheck,
  CheckSquare,
  ChevronRight,
  ChevronsUpDown,
  CircleDot,
  Clock,
  Contact,
  Copy,
  CreditCard,
  Database,
  Download,
  ExternalLink,
  Eye,
  FileCheck,
  FileDown,
  FilePlus2,
  FileSignature,
  FileText,
  Filter,
  Folder,
  FolderInput,
  FolderOpen,
  FolderPlus,
  FolderTree,
  GitBranch,
  GitCompare,
  GitMerge,
  HardDrive,
  History,
  Image,
  Info,
  Kanban,
  KeyRound,
  Landmark,
  Languages,
  Layers,
  LayoutGrid,
  LayoutList,
  Lightbulb,
  Link2,
  ListChecks,
  ListFilter,
  ListTodo,
  Lock,
  LogOut,
  Mail,
  MapPin,
  MapPinPlus,
  MessageCircle,
  MessageSquare,
  MousePointerClick,
  Move,
  Move3d,
  Network,
  Newspaper,
  Palette,
  Paperclip,
  PenLine,
  Pencil,
  PieChart,
  Plug,
  Plus,
  Receipt,
  RefreshCw,
  Reply,
  Ruler,
  Save,
  Scissors,
  ScrollText,
  Search,
  Send,
  Settings2,
  Share2,
  ShieldCheck,
  ShieldOff,
  Sparkles,
  SquareStack,
  Tag,
  Trash2,
  Undo2,
  Upload,
  User,
  UserCog,
  UserMinus,
  UserPlus,
  Users,
  Wand2,
  X,
  type LucideIcon,
} from "lucide-react";

import { PERMISSIONS, type Permission } from "@/constants/permissions";
import type { usePermissions } from "@/hooks/usePermissions";
import type { Translations } from "@/lib/i18n/types";
import type { PageInfoAreaId, PageInfoId, PageInfoItemId } from "@/lib/i18n/types/pageInfo";
import type { PageInfoTargetKey } from "./pageInfoTarget";

/** Settings → Help: every page's guide in one place ("More in the Help centre"). */
export const PAGE_INFO_GUIDE_ROUTE = "/settings/help";

export interface PageInfoGateContext {
  perms: ReturnType<typeof usePermissions>;
  /** The project the page is scoped to; null on a whole-building page. */
  projectId: number | null;
  /** The building the page belongs to; null outside a building. */
  buildingId: number | null;
}

export interface PageInfoItemDef<P extends PageInfoId> {
  id: PageInfoItemId<P>;
  icon: LucideIcon;
  /** The control "Show me" points at. Offered only while it is on screen. */
  target?: PageInfoTargetKey;
  visibleWhen?: (ctx: PageInfoGateContext) => boolean;
}

/** Groups for the Settings → Help guide, in the order it lists them. */
export const PAGE_INFO_AREAS: readonly PageInfoAreaId[] = [
  "buildings",
  "projects",
  "documents",
  "issues",
  "viewers",
  "account",
  "organisation",
  "billing",
];

export interface PageInfoPageDef<P extends PageInfoId> {
  title: (t: Translations) => string;
  /** Where the Help guide lists the page; the guide's own page has none. */
  area?: PageInfoAreaId;
  /**
   * How search results from another page name this page, when the title alone
   * is too bare out of context ("Overview" → "Billing: Overview").
   */
  searchTitle?: (t: Translations) => string;
  items: ReadonlyArray<PageInfoItemDef<P>>;
  /**
   * Whether the reader can open this page at all. Search uses it to keep other
   * pages' tasks out of the results for readers the page would turn away.
   */
  visibleWhen?: (ctx: PageInfoGateContext) => boolean;
  /**
   * Where the page lives, for search results from another page. Null when the
   * scope lacks what the URL needs (a building page searched from outside one).
   */
  route: (scope: PageInfoRouteScope) => string | null;
}

export type PageInfoRouteScope = Omit<PageInfoGateContext, "perms">;

/** A page under /buildings/:id, keeping the reader's project scope. */
const inBuilding =
  (path: string) =>
  ({ buildingId, projectId }: PageInfoRouteScope): string | null =>
    buildingId == null
      ? null
      : `/buildings/${buildingId}${path}${projectId != null ? `?project=${projectId}` : ""}`;
const fixed = (path: string) => () => path;

type ProjectCheck = (projectId: number) => boolean;

/** A project-scoped gate, falling back to the role's grant when no project is in scope. */
function inProject(
  ctx: PageInfoGateContext,
  check: (perms: PageInfoGateContext["perms"]) => ProjectCheck,
  code: Permission
): boolean {
  return ctx.projectId != null ? check(ctx.perms)(ctx.projectId) : ctx.perms.hasPermission(code);
}

const canWriteFiles = (ctx: PageInfoGateContext) =>
  ctx.projectId != null
    ? ctx.perms.canWriteFilesInProject(ctx.projectId)
    : ctx.perms.canUploadDocuments;
const canWriteFolders = (ctx: PageInfoGateContext) =>
  inProject(ctx, (p) => p.canWriteFoldersInProject, PERMISSIONS.PROJECT_WRITE_FOLDERS);
// Organise and drag-to-move: `canOrganizeFolder` / `canReorganizeProject` in the folder view.
const canReorganise = (ctx: PageInfoGateContext) =>
  ctx.perms.canModifyData ||
  (ctx.projectId != null && ctx.perms.canModifyFilesInProject(ctx.projectId));
const canManagePM = (ctx: PageInfoGateContext) =>
  inProject(ctx, (p) => p.canManagePMOnProject, PERMISSIONS.MANAGE_PM);
const canManageBriefs = (ctx: PageInfoGateContext) =>
  inProject(ctx, (p) => p.canManageBriefsOnProject, PERMISSIONS.MANAGE_BRIEFS);
const canManageContacts = (ctx: PageInfoGateContext) =>
  inProject(ctx, (p) => p.canManageContactsOnProject, PERMISSIONS.MANAGE_CONTACTS);
const canManageBuildingData = (ctx: PageInfoGateContext) =>
  ctx.buildingId != null && ctx.perms.canManageBuildingDataSidebar(ctx.buildingId);
const canManageMembers = (ctx: PageInfoGateContext) => ctx.perms.canManageMembers;
const canViewPM = (ctx: PageInfoGateContext) => ctx.perms.canViewPMTool;
const canManageOrg = (ctx: PageInfoGateContext) => ctx.perms.canManageOrgPermissions;
// OrganizationSettings' isBillingManager: the billing RPCs answer only these two roles.
const isBillingManager = (ctx: PageInfoGateContext) => ctx.perms.isOrgOwner || ctx.perms.isOrgAdmin;
const isOrgOwner = (ctx: PageInfoGateContext) => ctx.perms.isOrgOwner;

export const PAGE_INFO: { [P in PageInfoId]: PageInfoPageDef<P> } = {
  buildings: {
    title: (t) => t.buildings.myBuildings,
    area: "buildings",
    route: fixed("/buildings"),
    items: [
      { id: "open", icon: Building2 },
      { id: "projects", icon: ChevronRight, target: "buildings.expandProjects" },
      { id: "search", icon: Search, target: "buildings.search" },
      {
        id: "add",
        icon: Plus,
        target: "buildings.add",
        visibleWhen: ({ perms }) => perms.canCreateBuildings || perms.canChooseBuildingPlan,
      },
      { id: "rename", icon: Pencil, visibleWhen: ({ perms }) => perms.canEditAllBuildings },
      {
        id: "archive",
        icon: Archive,
        visibleWhen: ({ perms }) => perms.canArchiveBuildings || perms.canRestoreBuildings,
      },
    ],
  },
  building: {
    title: (t) => t.buildings.sidebar.building,
    area: "buildings",
    route: inBuilding(""),
    visibleWhen: ({ perms }) => !perms.isSubcontractorOnly,
    items: [
      { id: "openProject", icon: LayoutGrid },
      {
        id: "newProject",
        icon: FolderPlus,
        target: "building.newProject",
        visibleWhen: ({ perms }) => perms.canCreateProjects,
      },
      {
        id: "manageProject",
        icon: Settings2,
        visibleWhen: ({ perms }) => perms.canModifyProjects || perms.canManageProjectStatus,
      },
      {
        id: "archiveProject",
        icon: ArchiveRestore,
        visibleWhen: ({ perms }) => perms.canArchiveProjects,
      },
      { id: "details", icon: Pencil },
      {
        id: "buildingData",
        icon: Database,
        target: "building.buildingData",
        visibleWhen: ({ perms, buildingId }) =>
          buildingId != null && perms.canViewBuildingDataSidebar(buildingId),
      },
      { id: "analytics", icon: BarChart3 },
      {
        id: "viewMode",
        icon: ArrowLeftRight,
        target: "layout.viewMode",
        visibleWhen: ({ perms }) => !perms.isSubcontractorOnly,
      },
      { id: "chat", icon: MessageCircle, target: "layout.chat" },
    ],
  },
  buildingData: {
    title: (t) => t.buildings.sidebar.buildingData,
    area: "buildings",
    route: inBuilding("/data"),
    visibleWhen: ({ perms, buildingId }) =>
      buildingId != null
        ? perms.canViewBuildingDataSidebar(buildingId)
        : !perms.isSubcontractorOnly,
    items: [
      {
        id: "review",
        icon: ListChecks,
        target: "buildingData.review",
        visibleWhen: canManageBuildingData,
      },
      { id: "inspect", icon: Eye },
      { id: "confirm", icon: Check, visibleWhen: canManageBuildingData },
      { id: "conflicts", icon: GitMerge, visibleWhen: canManageBuildingData },
      { id: "confirmSection", icon: CheckSquare, visibleWhen: canManageBuildingData },
      { id: "fillMissing", icon: PenLine, visibleWhen: canManageBuildingData },
      { id: "filter", icon: ListFilter, target: "buildingData.filter" },
      { id: "search", icon: Search, target: "buildingData.search" },
      { id: "history", icon: History, target: "buildingData.history" },
    ],
  },
  editBuilding: {
    title: (t) => t.buildings.edit,
    area: "buildings",
    route: inBuilding("/edit"),
    visibleWhen: ({ perms }) => perms.canModifyData,
    items: [
      { id: "fields", icon: Pencil },
      { id: "address", icon: MapPin },
      { id: "save", icon: Save, target: "editBuilding.save" },
    ],
  },
  projectDashboard: {
    title: (t) => t.buildings.projectsDashboard.title,
    area: "projects",
    route: inBuilding("/project-dashboard"),
    items: [
      { id: "messages", icon: MessageSquare, target: "dashboard.messages" },
      { id: "replyReact", icon: Reply },
      { id: "whatsNew", icon: Newspaper, target: "dashboard.whatsNewRange" },
      {
        id: "myTasks",
        icon: CheckSquare,
        target: "dashboard.myTasksWindow",
        visibleWhen: canViewPM,
      },
      {
        id: "brief",
        icon: FileText,
        visibleWhen: ({ perms, projectId }) =>
          projectId != null ? perms.canViewBriefsOnProject(projectId) : perms.canViewBriefs,
      },
      { id: "analytics", icon: BarChart3 },
      { id: "switchProject", icon: ChevronsUpDown, target: "layout.projectSwitcher" },
    ],
  },
  documents: {
    title: (t) => t.buildings.sidebar.documents,
    area: "documents",
    route: inBuilding("/documents"),
    items: [
      { id: "upload", icon: Upload, target: "files.upload", visibleWhen: canWriteFiles },
      { id: "views", icon: LayoutList, target: "documents.views" },
      { id: "search", icon: Search, target: "files.search" },
      { id: "filters", icon: Filter, target: "documents.filters" },
      { id: "selection", icon: CheckSquare },
      {
        id: "newDocument",
        icon: FilePlus2,
        target: "files.newDocument",
        visibleWhen: canWriteFiles,
      },
      { id: "versions", icon: Layers },
      { id: "comments", icon: MessageSquare },
      { id: "verify", icon: BadgeCheck, visibleWhen: canReorganise },
      { id: "organise", icon: Wand2, target: "files.organise", visibleWhen: canReorganise },
      { id: "share", icon: Share2 },
      {
        id: "trash",
        icon: Trash2,
        target: "files.more",
        visibleWhen: ({ perms, projectId }) =>
          projectId != null || perms.hasPermission(PERMISSIONS.DELETE_DATA),
      },
      {
        id: "history",
        icon: History,
        target: "documents.history",
        visibleWhen: ({ perms, projectId }) =>
          projectId != null
            ? perms.canViewLogOnProject(projectId)
            : !perms.isSubcontractorOnly && perms.hasPermission(PERMISSIONS.PROJECT_VIEW_LOG),
      },
    ],
  },
  projectFolders: {
    title: (t) => t.documents.viewToggle.folders,
    area: "projects",
    route: ({ projectId }) => (projectId != null ? `/projects/${projectId}` : null),
    items: [
      { id: "browse", icon: FolderTree, target: "files.tree" },
      { id: "upload", icon: Upload, target: "files.upload", visibleWhen: canWriteFiles },
      {
        id: "newFolder",
        icon: FolderPlus,
        target: "files.newFolder",
        visibleWhen: canWriteFolders,
      },
      { id: "search", icon: Search, target: "files.search" },
      { id: "selection", icon: CheckSquare },
      { id: "folderMenu", icon: MousePointerClick },
      { id: "move", icon: FolderInput, visibleWhen: canReorganise },
      { id: "share", icon: Share2 },
      { id: "versions", icon: History },
      { id: "organise", icon: Wand2, target: "files.organise", visibleWhen: canReorganise },
      { id: "specialFolders", icon: Lock },
      { id: "trash", icon: Trash2, target: "files.more" },
    ],
  },
  projectLog: {
    title: (t) => t.pageInfo.projectLogTitle,
    area: "projects",
    route: ({ projectId }) => (projectId != null ? `/projects/${projectId}/log` : null),
    visibleWhen: (ctx) =>
      inProject(ctx, (p) => p.canViewLogOnProject, PERMISSIONS.PROJECT_VIEW_LOG),
    items: [
      { id: "read", icon: ScrollText },
      { id: "live", icon: Activity },
      { id: "archived", icon: Archive },
    ],
  },
  signing: {
    title: (t) => t.signing.title,
    area: "projects",
    route: inBuilding("/signing"),
    items: [
      { id: "signTurn", icon: PenLine, target: "signing.signNow" },
      { id: "tabs", icon: Users, target: "signing.tabs" },
      { id: "details", icon: Eye },
      { id: "statuses", icon: CircleDot },
      { id: "filter", icon: Filter, target: "signing.filter" },
      { id: "search", icon: Search, target: "signing.search" },
      { id: "sort", icon: ArrowUpDown },
      { id: "download", icon: Download, target: "signing.download" },
      { id: "selection", icon: CheckSquare },
      { id: "status", icon: RefreshCw, target: "signing.checkStatus" },
      {
        id: "start",
        icon: FileSignature,
        visibleWhen: ({ perms }) =>
          perms.isPlatformAdmin || perms.hasPermission(PERMISSIONS.ALLOW_SIGNING),
      },
      { id: "unfiled", icon: Info },
    ],
  },
  brief: {
    title: (t) => t.brief.title,
    area: "projects",
    route: inBuilding("/brief"),
    visibleWhen: ({ perms }) => perms.canViewBriefs,
    items: [
      { id: "start", icon: Sparkles, target: "brief.start", visibleWhen: canManageBriefs },
      { id: "addFiles", icon: Upload, target: "files.upload", visibleWhen: canManageBriefs },
      {
        id: "additions",
        icon: MessageSquare,
        target: "brief.additions",
        visibleWhen: canManageBriefs,
      },
      { id: "check", icon: Wand2, target: "brief.check", visibleWhen: canManageBriefs },
      { id: "suggestions", icon: Lightbulb, target: "brief.suggestions" },
      { id: "resolveSuggestion", icon: CheckCheck, visibleWhen: canManageBriefs },
      { id: "suggestedIssue", icon: ListTodo, visibleWhen: canManagePM },
      { id: "additionThread", icon: Reply, visibleWhen: canManageBriefs },
      { id: "confirm", icon: BadgeCheck, target: "brief.confirm", visibleWhen: canManageBriefs },
      { id: "versions", icon: GitCompare, target: "brief.versions" },
      { id: "history", icon: History, target: "brief.history" },
      { id: "deleteVersion", icon: Trash2, visibleWhen: canManageBriefs },
    ],
  },
  contacts: {
    title: (t) => t.contacts.title,
    area: "projects",
    route: inBuilding("/contacts"),
    visibleWhen: ({ perms }) => perms.hasPermission(PERMISSIONS.VIEW_CONTACTS),
    items: [
      { id: "add", icon: UserPlus, target: "contacts.add", visibleWhen: canManageContacts },
      { id: "memberDetails", icon: Contact },
      { id: "search", icon: Search, target: "contacts.search" },
      { id: "filter", icon: Filter, target: "contacts.filter" },
      { id: "edit", icon: Pencil },
      { id: "copy", icon: Copy },
      { id: "badges", icon: Users },
      { id: "sections", icon: FolderOpen, visibleWhen: ({ perms }) => !perms.isSubcontractorOnly },
    ],
  },
  model: {
    title: (t) => t.buildings.sidebar.model3d,
    area: "viewers",
    route: inBuilding("/model"),
    items: [
      { id: "models", icon: Box, target: "model.picker" },
      { id: "inspect", icon: MousePointerClick },
      { id: "spatialTree", icon: Network, target: "model.tree" },
      { id: "section", icon: Scissors, target: "model.section" },
      { id: "sectionOptions", icon: Settings2 },
      { id: "filters", icon: Filter, target: "model.filters" },
      {
        id: "issueFromElement",
        icon: Link2,
        visibleWhen: (ctx) => ctx.perms.canViewPMTool && canManagePM(ctx),
      },
      { id: "modelIssues", icon: ListChecks, target: "model.issues", visibleWhen: canViewPM },
      { id: "compare", icon: GitCompare },
      { id: "compareChanges", icon: ListFilter },
      { id: "navigate", icon: Move3d },
      { id: "screenshot", icon: Camera, target: "model.screenshot" },
    ],
  },
  drawings: {
    title: (t) => t.tools.drawingCompare.navLabel,
    area: "viewers",
    route: inBuilding("/drawings"),
    visibleWhen: ({ perms }) => perms.canViewDrawingCompareTool,
    items: [
      { id: "open", icon: FileText, target: "drawings.picker" },
      { id: "compare", icon: GitCompare, target: "drawings.picker" },
      { id: "modes", icon: SquareStack, target: "drawings.modes" },
      { id: "legend", icon: Palette },
      { id: "navigate", icon: Move },
      { id: "measure", icon: Ruler, target: "drawings.measure" },
      { id: "layers", icon: Layers, target: "drawings.layers" },
      {
        id: "issues",
        icon: MapPinPlus,
        target: "drawings.createIssue",
        visibleWhen: (ctx) => ctx.perms.canViewPMTool && canManagePM(ctx),
      },
      { id: "issueMarkers", icon: MapPin, visibleWhen: canViewPM },
      { id: "export", icon: FileDown, target: "drawings.export" },
    ],
  },
  issues: {
    title: (t) => t.pm.issues.title,
    area: "issues",
    route: inBuilding("/issues"),
    visibleWhen: canViewPM,
    items: [
      { id: "newIssue", icon: Plus, target: "issues.newIssue", visibleWhen: canManagePM },
      { id: "open", icon: FileText },
      { id: "views", icon: Kanban, target: "issues.views" },
      { id: "filters", icon: Filter, target: "issues.filters" },
      { id: "bulkEdit", icon: CheckSquare, visibleWhen: canManagePM },
      { id: "comments", icon: MessageSquare },
      { id: "attachments", icon: Paperclip, visibleWhen: canManagePM },
      { id: "statuses", icon: Check, visibleWhen: canManagePM },
      { id: "checklists", icon: ListChecks, visibleWhen: canManagePM },
      { id: "dependencies", icon: GitBranch, visibleWhen: canManagePM },
      { id: "snapshots", icon: Camera, visibleWhen: canManagePM },
      { id: "protocol", icon: FileDown, target: "issues.protocol" },
    ],
  },
  myIssues: {
    title: (t) => t.pm.myIssues.title,
    area: "issues",
    route: inBuilding("/my-issues"),
    visibleWhen: canViewPM,
    items: [
      { id: "tabs", icon: User, target: "myIssues.tabs" },
      { id: "open", icon: FileText },
      { id: "newIssue", icon: Plus, target: "issues.newIssue", visibleWhen: canManagePM },
      { id: "layout", icon: Kanban, target: "myIssues.layout" },
      { id: "filters", icon: Filter, target: "issues.filters" },
      { id: "myDisciplines", icon: Tag, target: "issues.myDisciplines" },
      { id: "sort", icon: ArrowUpDown, target: "issues.sort" },
      { id: "quickEdit", icon: MousePointerClick, visibleWhen: canManagePM },
      { id: "bulk", icon: CheckSquare, visibleWhen: canManagePM },
    ],
  },
  notifications: {
    title: (t) => t.pm.notifications.title,
    area: "issues",
    route: inBuilding("/notifications"),
    visibleWhen: canViewPM,
    items: [
      { id: "open", icon: Bell },
      { id: "filter", icon: Filter, target: "notifications.filter" },
      { id: "unread", icon: BellDot },
      { id: "older", icon: Clock },
      { id: "live", icon: RefreshCw },
      { id: "email", icon: Mail },
    ],
  },
  settingsPersonal: {
    title: (t) => t.settings.title,
    area: "account",
    route: fixed("/settings"),
    items: [
      { id: "profile", icon: User, target: "settings.save" },
      { id: "photo", icon: Image, target: "settings.photo" },
      { id: "avatarColour", icon: Palette },
      { id: "notifications", icon: Mail, target: "settings.notifications" },
      { id: "chatbot", icon: Bot, target: "settings.chatbot" },
      { id: "language", icon: Languages },
    ],
  },
  settingsSecurity: {
    title: (t) => t.settings.security,
    area: "account",
    route: fixed("/settings?section=security"),
    items: [
      { id: "password", icon: KeyRound, target: "settings.password" },
      { id: "signOut", icon: LogOut },
    ],
  },
  settingsInvitations: {
    title: (t) => t.common.invitations,
    area: "account",
    route: fixed("/settings?section=invitations"),
    items: [
      { id: "accept", icon: Check, target: "invitations.accept" },
      { id: "decline", icon: X },
      { id: "switchOrg", icon: Building2 },
      { id: "missing", icon: Clock },
    ],
  },
  settingsConnections: {
    title: (t) => t.connectors.connectedTitle,
    area: "account",
    route: fixed("/settings?section=connections"),
    items: [
      { id: "review", icon: Plug },
      { id: "revoke", icon: ShieldOff, target: "connections.revoke" },
      { id: "connect", icon: Link2 },
      { id: "approveChange", icon: ShieldCheck },
      { id: "appUpload", icon: Upload },
    ],
  },
  orgSettingsGeneral: {
    title: (t) => t.settings.organizationTitle,
    area: "organisation",
    route: fixed("/organization-settings"),
    visibleWhen: canManageOrg,
    items: [
      { id: "rename", icon: Pencil, target: "org.save" },
      { id: "logo", icon: Image, target: "org.logo", visibleWhen: isOrgOwner },
      { id: "switchOrg", icon: Building },
    ],
  },
  orgSettingsUsers: {
    title: (t) => t.common.userManagement,
    area: "organisation",
    route: fixed("/organization-settings/users"),
    visibleWhen: canManageOrg,
    items: [
      { id: "invite", icon: Send, target: "org.invite", visibleWhen: canManageMembers },
      { id: "role", icon: UserCog, visibleWhen: canManageMembers },
      { id: "projectAccess", icon: FolderTree, visibleWhen: canManageMembers },
      { id: "remove", icon: UserMinus, visibleWhen: canManageMembers },
      { id: "invitations", icon: Mail, visibleWhen: canManageMembers },
      { id: "members", icon: Users, target: "org.refreshMembers" },
    ],
  },
  orgSettingsUsage: {
    title: (t) => t.settings.usage.title,
    area: "organisation",
    route: fixed("/organization-settings?section=usage"),
    visibleWhen: canManageOrg,
    items: [
      { id: "storage", icon: HardDrive },
      { id: "limits", icon: AlertTriangle },
      { id: "byProject", icon: PieChart },
      { id: "activationPending", icon: Clock },
    ],
  },
  billingOverview: {
    title: (t) => t.billing.page.overviewTitle,
    area: "billing",
    searchTitle: (t) => `${t.billing.nav.group}: ${t.billing.page.overviewTitle}`,
    route: fixed("/billing"),
    visibleWhen: isBillingManager,
    items: [
      { id: "summary", icon: CalendarClock },
      { id: "addBuilding", icon: Plus, target: "billing.addBuilding", visibleWhen: isOrgOwner },
      { id: "planLimits", icon: Info },
      { id: "changePlan", icon: Settings2, visibleWhen: isOrgOwner },
      { id: "removeBuilding", icon: Trash2, visibleWhen: isOrgOwner },
      { id: "restoreBuilding", icon: Undo2, target: "billing.restore", visibleWhen: isOrgOwner },
      { id: "activation", icon: RefreshCw, visibleWhen: isOrgOwner },
      { id: "notices", icon: Bell },
      { id: "transfer", icon: ArrowRightLeft },
    ],
  },
  billingDetails: {
    title: (t) => t.billing.page.detailsTitle,
    area: "billing",
    route: fixed("/billing/details"),
    visibleWhen: isBillingManager,
    items: [
      {
        id: "companyDetails",
        icon: Landmark,
        target: "billing.saveDetails",
        visibleWhen: isOrgOwner,
      },
      {
        id: "paymentMethod",
        icon: CreditCard,
        target: "billing.paymentMethod",
        visibleWhen: isOrgOwner,
      },
      { id: "continueAdding", icon: Building2, visibleWhen: isOrgOwner },
      { id: "notices", icon: Bell },
    ],
  },
  billingInvoices: {
    title: (t) => t.billing.page.invoicesTitle,
    area: "billing",
    route: fixed("/billing/invoices"),
    visibleWhen: isBillingManager,
    items: [
      { id: "download", icon: Receipt, target: "billing.downloadInvoice" },
      { id: "status", icon: FileCheck },
      { id: "overdue", icon: Bell },
    ],
  },
  help: {
    title: (t) => t.common.help,
    route: fixed(PAGE_INFO_GUIDE_ROUTE),
    items: [
      { id: "search", icon: Search },
      { id: "browse", icon: BookOpen },
      { id: "openPage", icon: ExternalLink },
      { id: "pageInfo", icon: Info },
    ],
  },
};
